import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Nav, FormCheck } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./SearchFilter.css";
import AlertDialog from "../AlertDialog";
import backgroundImage from "../../assets/images/window 01.png";
import carimg from "../../assets/images/car-two.png";
import carone from "../../assets/images/car-three.png";
import cartwo from "../../assets/images/car-four.png";
import carthree from "../../assets/images/car-five.png";
import carfour from "../../assets/images/car-six.png";
import Swal from "sweetalert2";

const API_BASE_URL = "https://motor-easy.clickbooster.io/api";

const SearchComponent = () => {
  const [vehicleType, setVehicleType] = useState('car');
  const [makers, setMakers] = useState([]);
  const [selectedCar, setSelectedCar] = useState('');
  const [selectedMaker, setSelectedMaker] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [models, setModels] = useState([]);
  const [cars, setCars] = useState([]);
  const [carType, setCarType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [carName, setCarName] = useState('');
  const [makerName, setMakerName] = useState('');
  const [modelName, setModelName] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('filter');
  const [engineSize, setEngineSize] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [currentKm, setCurrentKm] = useState('');
  const [needsDropdownUpdate, setNeedsDropdownUpdate] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [lastSelectedValues, setLastSelectedValues] = useState({
    carId: '',
    makerId: '',
    modelId: null,
  });

  const navigate = useNavigate();
  const API_BASE_URL = 'your-api-base-url'; // Replace with your actual API base URL

  // Fetch cars
  useEffect(() => {
    const fetchCars = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/cars`);
        if (response.data && Array.isArray(response.data)) {
          setCars(response.data);
          if (carName && needsDropdownUpdate) {
            const carId = findCarIdByName(carName);
            if (carId) {
              setSelectedCar(carId);
              setNeedsDropdownUpdate(false);
            }
          }
          console.log('Vehicle data loaded successfully.');
        } else {
          console.error('Unexpected response data format:', response.data);
          toast.error('Unable to load vehicle data. Please refresh the page.');
        }
      } catch (error) {
        console.error('Error fetching car data:', error);
        toast.error('Unable to connect to the vehicle database.');
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, [carName, needsDropdownUpdate]);

  // Fetch makers
  useEffect(() => {
    const fetchMakers = async () => {
      if (!selectedCar) return;
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/maker?car_id=${selectedCar}`);
        if (response.data && Array.isArray(response.data.makers)) {
          setMakers(response.data.makers);
        } else {
          console.error('Unexpected response data format:', response.data.makers);
          toast.error('Unable to load makers.');
        }
      } catch (error) {
        console.error('Error fetching makers:', error);
        toast.error('Error fetching makers.');
      } finally {
        setLoading(false);
      }
    };

    fetchMakers();
  }, [selectedCar]);

  // Fetch models
  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedCar || !selectedMaker) return;
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/models`, {
          params: { car_id: selectedCar, maker_id: selectedMaker },
        });
        if (response.data && Array.isArray(response.data)) {
          setModels(response.data);
        } else {
          console.error('Unexpected response data format:', response.data);
          toast.error('Unable to load models.');
        }
      } catch (error) {
        console.error('Error fetching models:', error);
        toast.error('Error fetching models.');
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, [selectedCar, selectedMaker]);

  // Handle CurrentKm change
  const handleCurrentKmChange = (e) => {
    const value = e.target.value;
    setCurrentKm(value);
    if (value && parseInt(value, 10) >= 180000) {
      Swal.fire({
        icon: 'warning',
        title: 'High Mileage',
        text: 'Your vehicle\'s current km is too high (≥ 180,000 km).',
        confirmButtonText: 'OK',
      });
    }
  };

  // Handle main search
  const handleFilterSearchMain = async () => {
    if (!selectedCar) {
      toast.error('Please select a car.');
      return;
    }
    if (!selectedMaker) {
      toast.error('Please select a make.');
      return;
    }
    if (!selectedYear) {
      toast.error('Please select a year.');
      return;
    }
    if (currentKm && parseInt(currentKm, 10) >= 180000) {
      Swal.fire({
        icon: 'warning',
        title: 'High Mileage',
        text: 'Your vehicle\'s current km is too high (≥ 180,000 km).',
        confirmButtonText: 'OK',
      });
      return;
    }

    try {
      setLoading(true);
      const selectedCarDetails = cars.find((car) => car.id.toString() === selectedCar.toString());
      if (!selectedCarDetails) {
        toast.error('Selected car details not found.');
        return;
      }

      const params = {
        car_name: selectedCarDetails.car_name,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        ...(selectedYear && {
          model_name: models.find((model) => model.id.toString() === selectedYear.toString())?.model_name,
        }),
        ...(engineSize && { engine_size: engineSize }),
        ...(fuelType && { fuel_type: fuelType }),
        ...(currentKm && { current_km: currentKm }),
        ...(carType && { car_type: carType }),
      };

      const response = await axios.get(`${API_BASE_URL}/entries/filter`, { params });
      if (response.data.status === false) {
        throw new Error(response.data.message || 'No products found');
      }

      toast.success(`Found ${response.data.data ? response.data.data.length : 0} products!`);
      setTimeout(() => {
        navigate('/product-details', {
          state: {
            data: response.data.data,
            selectedVehicle: {
              car: selectedCarDetails.car_name,
              fromDate,
              toDate,
              year: selectedYear
                ? models.find((model) => model.id.toString() === selectedYear.toString())?.model_name
                : null,
              engineSize,
              fuelType,
              currentKm,
              carType,
            },
          },
        });
      }, 1800);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(error.message || 'Failed to fetch products');
      navigate('/product-details', { state: { error: error.message || 'Failed to fetch products' } });
    } finally {
      setLoading(false);
    }
  };

  // Handle search button with confirmation
  const handleSearchButtonClick = () => {
    if (!selectedCar) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a car to continue.',
        confirmButtonText: 'OK',
      });
      return;
    }
    Swal.fire({
      icon: 'question',
      title: 'Confirm Search',
      text: 'Ready to search for products matching your vehicle?',
      showCancelButton: true,
      confirmButtonText: 'Search Now',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        handleFilterSearchMain();
      }
    });
  };

  // Handle car model change
  const handleCarModelChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Handle vehicle type change
  const handleVehicleTypeChange = (type) => {
    setVehicleType(type);
  };

  // Handle engine size change
  const handleEngineSizeChange = (e) => {
    setEngineSize(e.target.value);
  };

  // Handle fuel type change
  const handleFuelTypeChange = (e) => {
    setFuelType(e.target.value);
  };

  // Find car ID by name
  const findCarIdByName = (name) => {
    if (!name || !cars || cars.length === 0) return '';
    let car = cars.find((car) => car.car_name.toLowerCase() === name.toLowerCase());
    if (!car) {
      car = cars.find(
        (car) =>
          car.car_name.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(car.car_name.toLowerCase())
      );
    }
    return car ? car.id : '';
  };

  const CarCardArr = [
    { image: carimg, name: 'Fortuner' },
    { image: carone, name: 'Jaguar' },
    { image: cartwo, name: 'V8' },
    { image: carthree, name: 'Honda' },
    { image: carfour, name: 'Grande' },
  ];

  return (
    <div
      className="adjust-padding container-fluid d-flex justify-content-center align-items-center"
      style={{
        minHeight: '100vh',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: 'white',
      }}
    >
      <Container fluid className="p-md-5 p-2" style={{ maxWidth: '1100px' }}>
        <Row className="text-center mb-4 mt-md-0 mt-2 g-0">
          <Col>
            <h1 className="fw-bold fs-1" style={{ color: '#002e5a' }}>
              Search a Product for your Vehicle
            </h1>
            <h4 style={{ color: '#002e5a' }}>
              Inspection, Service, Warranty
            </h4>
          </Col>
        </Row>

        {activeTab === 'filter' && (
          <Form>
            <Row className="mb-3 g-0">
              <div className="row row-style">
                <div className="row">
                  <div className="col-lg-6">
                    <Col
                      xs={12}
                      md={12}
                      className="d-flex flex-wrap justify-content-between"
                      style={{ color: '#002e5a' }}
                    >
                      <Form.Label>Vehicle</Form.Label>
                      <div className="ms-4 d-flex flex-wrap justify-content-evenly">
                        <FormCheck
                          type="radio"
                          label="Car"
                          name="vehicleType"
                          id="car"
                          inline
                          checked={vehicleType === 'car'}
                          onChange={() => handleVehicleTypeChange('car')}
                        />
                        <FormCheck
                          type="radio"
                          label="Van"
                          name="vehicleType"
                          id="Van"
                          inline
                          checked={vehicleType === 'Van'}
                          onChange={() => handleVehicleTypeChange('Van')}
                        />
                        <FormCheck
                          type="radio"
                          label="Motorhome"
                          name="vehicleType"
                          id="motorhome"
                          inline
                          checked={vehicleType === 'motorhome'}
                          onChange={() => handleVehicleTypeChange('motorhome')}
                        />
                        <FormCheck
                          type="radio"
                          label="Motorcycle"
                          name="vehicleType"
                          id="motorcycle"
                          inline
                          checked={vehicleType === 'motorcycle'}
                          onChange={() => handleVehicleTypeChange('motorcycle')}
                        />
                      </div>
                    </Col>
                  </div>
                </div>
                <div className="mt-3 col-lg-6">
                  <Col xs={12} md={12}>
                    <Form.Group
                      className="row mt-2 justify-content-center d-flex align-items-center"
                      style={{ color: '#002e5a' }}
                      controlId="make"
                    >
                      <div className="col-sm-3">
                        <Form.Label>Make</Form.Label>
                      </div>
                      <div className="col-sm-9">
                        <Form.Control
                          style={{ padding: '15px' }}
                          as="select"
                          value={selectedCar}
                          onChange={(e) => setSelectedCar(e.target.value)}
                          required
                        >
                          <option value="">Make</option>
                          {cars?.map((car) => (
                            <option key={car.id} value={car.id}>
                              {car.car_name}
                            </option>
                          ))}
                        </Form.Control>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={12}>
                    <Form.Group
                      className="row mt-2 justify-content-center d-flex align-items-center"
                      style={{ color: '#002e5a' }}
                      controlId="model"
                    >
                      <div className="col-sm-3">
                        <Form.Label>Model</Form.Label>
                      </div>
                      <div className="col-sm-9">
                        <Form.Control
                          style={{ padding: '15px' }}
                          as="select"
                          value={selectedMaker}
                          onChange={(e) => setSelectedMaker(e.target.value)}
                          required
                        >
                          <option value="">Model</option>
                          {makers.map((maker) => (
                            <option key={maker.id} value={maker.id}>
                              {maker.maker_name}
                            </option>
                          ))}
                        </Form.Control>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={12}>
                    <Form.Group
                      className="row mt-2 justify-content-center d-flex align-items-center"
                      style={{ color: '#002e5a' }}
                      controlId="yearSelect"
                    >
                      <div className="col-sm-3" style={{ color: '#002e5a' }}>
                        <Form.Label>Year</Form.Label>
                      </div>
                      <div className="col-sm-9">
                        <Form.Control
                          as="select"
                          id="year"
                          value={selectedYear}
                          onChange={handleCarModelChange}
                          style={{ padding: '15px' }}
                          required
                        >
                          <option value="">Year</option>
                          {models.map((model, index) => (
                            <option key={index} value={model.id}>
                              {model.model_name}
                            </option>
                          ))}
                        </Form.Control>
                      </div>
                    </Form.Group>
                  </Col>
                </div>

                <div className="col-lg-6">
                  <Col xs={12} md={12}>
                    <Form.Group
                      className="row mt-2 justify-content-center d-flex align-items-center mt-4"
                      controlId="EngineSize"
                    >
                      <div className="col-sm-3">
                        <Form.Label>Engine Size</Form.Label>
                      </div>
                      <div className="col-sm-9">
                        <Form.Control
                          as="select"
                          value={engineSize}
                          onChange={handleEngineSizeChange}
                          style={{ padding: '15px' }}
                          required
                        >
                          <option value="">Engine Size</option>
                          <option value="3 cylinder">3 cylinder</option>
                          <option value="4 cylinder">4 cylinder</option>
                          <option value="5 cylinder">5 cylinder</option>
                          <option value="6 cylinder">6 cylinder</option>
                        </Form.Control>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Group
                      className="row mt-2 justify-content-center align-items-center"
                      controlId="CurrentKm"
                    >
                      <div className="col-sm-3" style={{ color: '#002e5a' }}>
                        <Form.Label>Current Km</Form.Label>
                      </div>
                      <div className="col-sm-9">
                        <Form.Control
                          type="number"
                          placeholder="Enter Current Km"
                          value={currentKm}
                          onChange={handleCurrentKmChange}
                          style={{
                            padding: '10px',
                            borderRadius: '5px',
                            borderColor: '#ced4da',
                            minHeight: '20px',
                          }}
                          required
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={12}>
                    <Form.Group
                      className="row mt-2 justify-content-center d-flex align-items-center"
                      style={{ color: '#002e5a' }}
                      controlId="FuelType"
                    >
                      <div className="col-sm-3">
                        <Form.Label>Fuel Type</Form.Label>
                      </div>
                      <div className="col-sm-9">
                        <Form.Control
                          as="select"
                          value={fuelType}
                          onChange={handleFuelTypeChange}
                          style={{ padding: '15px' }}
                          required
                        >
                          <option value="">Fuel Type</option>
                          <option value="Petrol">Petrol</option>
                          <option value="Diesel">Diesel</option>
                          <option value="Hybrid">Hybrid</option>
                          <option value="EV">EV</option>
                        </Form.Control>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col xs={12} className="text-end mt-2">
                    <Button
                      variant="primary"
                      onClick={handleSearchButtonClick}
                      disabled={loading || (currentKm && parseInt(currentKm, 10) >= 180000)}
                      style={{
                        backgroundColor: '#007bff',
                        padding: '10px 70px',
                        marginTop: '30px',
                        fontSize: '1.2rem',
                        borderRadius: '5px',
                      }}
                    >
                      {loading ? 'Loading...' : 'Show'}
                    </Button>
                  </Col>
                </div>

                <div className="img d-flex gap-3 mb-3">
                  {CarCardArr.map(({ image, name }, i) => (
                    <CarCard key={i} image={image} name={name} />
                  ))}
                </div>
              </div>
            </Row>
          </Form>
        )}
      </Container>
    </div>
  );
};

const CarCard = ({ name, image }) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",      // centers image & text horizontally inside card
        justifyContent: "space-between",  // centers content vertically inside card
        width: "160px",
        height: "auto",
        borderRadius: "8px",
        padding: "10px",
        boxSizing: "border-box",
      }}
    >
      <div style={{width: '130px', height: "130px", display: 'flex', alignItems: 'center'}}>
        <img
        src={image}
        alt={name}
        style={{
          width: "100%",
          height: "auto",
          objectFit: "contain",
          objectPosition:'center'
        }}
      />
      </div>
      <p
        style={{
          marginTop: "8px",   // fix spacing typo here (was "8 px")
          fontWeight: "600",
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        {name}
      </p>
    </div>
  );
};


export default SearchComponent;
